import React, { useState, useEffect } from 'react';
import { 
  Button, 
  TextField, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Box, 
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  FormHelperText,
  Autocomplete,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { getAuthHeaders } from '../../utils/auth';

const LessonTypeManagement = () => {
  const [lessonTypes, setLessonTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [availableColors, setAvailableColors] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [showColorCreation, setShowColorCreation] = useState(false);
  const [newColorData, setNewColorData] = useState({
    name: '',
    label: '',
    hex_value: '#02c2c7',
    is_active: true
  });
  const [formData, setFormData] = useState({
    name: '',
    friendly_name: '',
    price: '',
    max_people: '',
    participants_per_booking: '',
    spot_price: '',
    bg_color: '',
    location_id: '',
    lesson_categories_id: [],
    create_price_list_entry: false,
    price_list_name: '',
    price_list_price: '',
    price_list_additional_info: '',
    selected_pass_type_ids: []
  });
  const [availablePassTypes, setAvailablePassTypes] = useState([]);
  const [loadingPassTypes, setLoadingPassTypes] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      console.log('Fetching data for lesson type management...');
      const headers = await getAuthHeaders();
      const [lessonTypesRes, locationsRes, categoriesRes, colorsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/lesson-type/`, {
          headers
        }),
        fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/lesson-type-locations/`, {
          headers
        }),
        fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/lesson-category/`, {
          headers
        }),
        fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/lesson-type-colors/?size=100`, {
          headers
        })
      ]);

      console.log('Response status:', {
        lessonTypes: lessonTypesRes.status,
        locations: locationsRes.status, 
        categories: categoriesRes.status,
        colors: colorsRes.status
      });

      if (lessonTypesRes.ok) {
        const data = await lessonTypesRes.json();
        console.log('Lesson types data:', data);
        setLessonTypes(data.objects || []);
      } else {
        console.error('Failed to fetch lesson types:', await lessonTypesRes.text());
      }
      
      if (locationsRes.ok) {
        const data = await locationsRes.json();
        console.log('Locations data:', data);
        setLocations(Array.isArray(data) ? data : (data.objects || []));
      } else {
        console.error('Failed to fetch locations:', await locationsRes.text());
      }
      
      if (categoriesRes.ok) {
        const data = await categoriesRes.json();
        console.log('Categories data:', data);
        setCategories(Array.isArray(data) ? data : (data.objects || []));
      } else {
        console.error('Failed to fetch categories:', await categoriesRes.text());
      }
      
      if (colorsRes.ok) {
        const data = await colorsRes.json();
        console.log('Colors data:', data);
        const activeColors = (data.objects || []).filter(color => color.is_active);
        setAvailableColors(activeColors);
        // Set default color if available
        if (activeColors.length > 0 && !formData.bg_color) {
          setFormData(prev => ({ ...prev, bg_color: activeColors[0].hex_value }));
        }
      } else {
        console.error('Failed to fetch colors:', await colorsRes.text());
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchPassTypesForCategories = async (lessonCategoryIds) => {
    if (!lessonCategoryIds || lessonCategoryIds.length === 0) {
      setAvailablePassTypes([]);
      return;
    }

    setLoadingPassTypes(true);
    try {
      const categoryIdsString = lessonCategoryIds.join(',');
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/pass-type/for-lesson-categories?lesson_category_ids=${categoryIdsString}`,
        {
          headers
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAvailablePassTypes(data.objects || []);
      } else {
        if (response.status === 403 || response.status === 401) {
          console.error('Brak uprawnień do pobrania typów karnetów');
        } else {
          console.error('Failed to fetch pass types:', await response.text());
        }
        setAvailablePassTypes([]);
      }
    } catch (error) {
      console.error('Error fetching pass types:', error);
      setAvailablePassTypes([]);
    } finally {
      setLoadingPassTypes(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // If lesson categories changed, fetch available pass types
    if (field === 'lesson_categories_id') {
      fetchPassTypesForCategories(value);
      // Clear selected pass types when categories change
      setFormData(prev => ({
        ...prev,
        [field]: value,
        selected_pass_type_ids: []
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        price: parseInt(formData.price),
        max_people: parseInt(formData.max_people),
        participants_per_booking: formData.participants_per_booking ? parseInt(formData.participants_per_booking) : null,
        // Single-spot price only makes sense for duo/trio; empty = whole bookings only.
        spot_price: formData.participants_per_booking && formData.spot_price !== '' ? parseInt(formData.spot_price) : null,
        location_id: formData.location_id || null,
        // Convert price_list_price to integer if it exists
        price_list_price: formData.price_list_price ? parseInt(formData.price_list_price) : null,
      };

      const method = editingType ? 'PUT' : 'POST';
      const url = editingType 
        ? `${process.env.REACT_APP_BACKEND_ADDRESS}/lesson-type/${editingType.id}`
        : `${process.env.REACT_APP_BACKEND_ADDRESS}/lesson-type/`;

      const headers = await getAuthHeaders();
      const response = await fetch(url, {
        method: method,
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        await fetchData();
        setOpen(false);
        resetForm();
        setEditingType(null);
        alert(editingType ? 'Typ zajęć został pomyślnie zaktualizowany!' : 'Typ zajęć został pomyślnie utworzony!');
      } else {
        if (response.status === 403 || response.status === 401) {
          alert('Brak uprawnień do zapisania typu zajęć');
        } else {
          const error = await response.text();
          alert(`Błąd podczas ${editingType ? 'aktualizacji' : 'tworzenia'} typu zajęć: ${error}`);
        }
      }
    } catch (error) {
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      friendly_name: '',
      price: '',
      max_people: '',
      participants_per_booking: '',
      spot_price: '',
      bg_color: availableColors.length > 0 ? availableColors[0].hex_value : '',
      location_id: '',
      lesson_categories_id: [],
      create_price_list_entry: false,
      price_list_name: '',
      price_list_price: '',
      price_list_additional_info: '',
      selected_pass_type_ids: []
    });
    setEditingType(null);
    setAvailablePassTypes([]);
    setLoadingPassTypes(false);
  };

  const handleEdit = (lessonType) => {
    setFormData({
      name: lessonType.name,
      friendly_name: lessonType.friendly_name || lessonType.name, // Use friendly_name or fallback to name
      price: lessonType.price.toString(),
      max_people: lessonType.max_people.toString(),
      participants_per_booking: lessonType.participants_per_booking ? lessonType.participants_per_booking.toString() : '',
      spot_price: lessonType.spot_price != null ? lessonType.spot_price.toString() : '',
      bg_color: lessonType.bg_color,
      location_id: lessonType.location?.id || '',
      lesson_categories_id: lessonType.lesson_categories_id || [],
      create_price_list_entry: false,
      price_list_name: '',
      price_list_price: '',
      price_list_additional_info: '',
      selected_pass_type_ids: []
    });
    setEditingType(lessonType);
    
    // Fetch pass types if lesson categories exist
    if (lessonType.lesson_categories_id && lessonType.lesson_categories_id.length > 0) {
      fetchPassTypesForCategories(lessonType.lesson_categories_id);
    }
    
    setOpen(true);
  };

  const handleDelete = async (lessonType) => {
    if (window.confirm(`Czy na pewno chcesz usunąć "${lessonType.name}"?`)) {
      try {
        const headers = await getAuthHeaders();
        const response = await fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/lesson-type/${lessonType.id}`, {
          method: 'DELETE',
          headers
        });

        if (response.ok) {
          await fetchData();
          alert('Typ zajęć został pomyślnie usunięty!');
        } else {
          if (response.status === 403 || response.status === 401) {
            alert('Brak uprawnień do usunięcia typu zajęć');
          } else {
            const error = await response.text();
            alert(`Błąd podczas usuwania typu zajęć: ${error}`);
          }
        }
      } catch (error) {
        alert(`Błąd: ${error.message}`);
      }
    }
  };


  const handleViewDetails = (lessonType) => {
    setSelectedType(lessonType);
    setDetailsOpen(true);
  };

  const handleCreateNewColor = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/lesson-type-colors/`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newColorData)
      });

      if (response.ok) {
        const createdColor = await response.json();
        // Refresh colors list
        await fetchData();
        // Set the newly created color as selected
        setFormData(prev => ({ ...prev, bg_color: createdColor.hex_value }));
        // Reset and hide color creation form
        setNewColorData({
          name: '',
          label: '',
          hex_value: '#02c2c7',
          is_active: true
        });
        setShowColorCreation(false);
        alert('Nowy kolor został utworzony pomyślnie!');
      } else {
        if (response.status === 403 || response.status === 401) {
          alert('Brak uprawnień do utworzenia koloru');
        } else {
          const error = await response.text();
          alert(`Błąd podczas tworzenia koloru: ${error}`);
        }
      }
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
  };

  const generateColorName = (label) => {
    // Generate a clean name from the label
    if (!label) return '';
    
    return label
      .toLowerCase()
      .trim()
      .replace(/&/g, 'and')           // Replace & with 'and'
      .replace(/\+/g, 'plus')         // Replace + with 'plus'
      .replace(/@/g, 'at')            // Replace @ with 'at'
      .replace(/%/g, 'percent')       // Replace % with 'percent'
      .replace(/\$/g, 'dollar')       // Replace $ with 'dollar'
      .replace(/[^\w\s-]/g, '')       // Remove special characters except word chars, spaces, and hyphens
      .replace(/\s+/g, '_')           // Replace spaces with underscores
      .replace(/-+/g, '_')            // Replace hyphens with underscores
      .replace(/_+/g, '_')            // Replace multiple underscores with single
      .replace(/^_|_$/g, '');         // Remove leading/trailing underscores
  };

  const generateLessonTypeName = (friendlyName) => {
    // Generate a clean name from the friendly name for lesson types
    if (!friendlyName) return '';
    
    return friendlyName
      .toLowerCase()
      .trim()
      .replace(/&/g, 'and')           // Replace & with 'and'
      .replace(/\+/g, 'plus')         // Replace + with 'plus'
      .replace(/@/g, 'at')            // Replace @ with 'at'
      .replace(/%/g, 'percent')       // Replace % with 'percent'
      .replace(/\$/g, 'dollar')       // Replace $ with 'dollar'
      .replace(/\#/g, 'hash')         // Replace # with 'hash'
      .replace(/\*/g, 'star')         // Replace * with 'star'
      .replace(/[^\w\s-]/g, '')       // Remove special characters except word chars, spaces, and hyphens
      .replace(/\s+/g, '_')           // Replace spaces with underscores
      .replace(/-+/g, '_')            // Replace hyphens with underscores
      .replace(/_+/g, '_')            // Replace multiple underscores with single
      .replace(/^_|_$/g, '');         // Remove leading/trailing underscores
  };

  const ColorPreview = ({ color }) => (
    <Box
      sx={{
        width: 20,
        height: 20,
        backgroundColor: color,
        borderRadius: '50%',
        display: 'inline-block',
        marginRight: 1,
        border: '1px solid #ccc'
      }}
    />
  );

  const getColorLabel = (hexValue) => {
    const color = availableColors.find(c => c.hex_value === hexValue);
    return color ? color.label : hexValue;
  };

  return (
    <>
      <Box>
        {/* Header */}
        <Box 
          display="flex" 
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="space-between" 
          alignItems={{ xs: "stretch", sm: "center" }} 
          mb={3}
          gap={2}
        >
          <Box>
            <Typography 
              variant="h5" 
              component="h2" 
              gutterBottom
              sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
            >
              Zarządzanie Typami Zajęć
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Twórz, edytuj i zarządzaj typami zajęć dostępnych w studiu
            </Typography>
          </Box>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => setOpen(true)}
            size="large"
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              px: { xs: 2, sm: 3 },
              whiteSpace: 'nowrap',
              minWidth: { sm: 'fit-content' },
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              Dodaj Nowy Typ Zajęć
            </Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
              Dodaj Typ
            </Box>
          </Button>
        </Box>

        {lessonTypes.length > 0 ? (
          <Box sx={{ overflowX: 'auto' }}>
            <TableContainer component={Paper} elevation={2} sx={{ minWidth: { xs: 600, sm: 'auto' } }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Kolor</TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, fontWeight: 600 }}>
                      Nazwa
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, fontWeight: 600 }}>
                      Cena
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, fontWeight: 600, display: { xs: 'none', md: 'table-cell' } }}>
                      Max osób
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, fontWeight: 600 }}>
                      Akcje
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lessonTypes.map(type => (
                    <TableRow 
                      key={type.id} 
                      hover
                      sx={{
                        '&:hover': {
                          backgroundColor: '#f8f9fa',
                        }
                      }}
                    >
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        {type.bg_color && <ColorPreview color={type.bg_color} />}
                      </TableCell>
                      <TableCell sx={{ padding: { xs: '8px 4px', sm: '16px' } }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          {/* Show color on mobile inline */}
                          <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                            {type.bg_color && <ColorPreview color={type.bg_color} />}
                          </Box>
                          <Box>
                            <Typography 
                              variant="body1" 
                              fontWeight={500}
                              sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}
                            >
                              {type.name}
                              {type.participants_per_booking >= 2 && (
                                <Chip
                                  size="small"
                                  color="secondary"
                                  label={`${type.participants_per_booking === 2 ? 'DUO' : 'TRIO'}${type.spot_price != null ? ' · miejsca' : ''}`}
                                  sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                                />
                              )}
                            </Typography>
                            {type.friendly_name && (
                              <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
                              >
                                ({type.friendly_name})
                              </Typography>
                            )}
                            {/* Show max people on mobile */}
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              sx={{ 
                                display: { xs: 'block', md: 'none' },
                                fontSize: '0.7rem'
                              }}
                            >
                              Max: {type.max_people}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ padding: { xs: '8px 4px', sm: '16px' } }}>
                        <Typography 
                          variant="body1"
                          sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}
                        >
                          {type.price} PLN
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body1">
                          {type.max_people}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ padding: { xs: '8px 4px', sm: '16px' } }}>
                        <Box display="flex" gap={0.5} justifyContent="center">
                          <IconButton
                            color="info"
                            size="small"
                            onClick={() => handleViewDetails(type)}
                            title="Pokaż szczegóły"
                            sx={{
                              '&:hover': {
                                backgroundColor: 'info.light',
                                color: 'white'
                              }
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => handleEdit(type)}
                            title="Edytuj"
                            sx={{
                              '&:hover': {
                                backgroundColor: 'primary.light',
                                color: 'white'
                              }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleDelete(type)}
                            title="Usuń"
                            sx={{
                              '&:hover': {
                                backgroundColor: 'error.light',
                                color: 'white'
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Brak typów zajęć
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Nie znaleziono żadnych typów zajęć. Dodaj pierwszy typ zajęć, aby zacząć.
            </Typography>
            <Button 
              variant="outlined" 
              color="primary" 
              onClick={() => setOpen(true)}
            >
              Dodaj Pierwszy Typ Zajęć
            </Button>
          </Paper>
        )}
      </Box>

      <Dialog open={open} onClose={() => { setOpen(false); resetForm(); }} maxWidth="md" fullWidth>
        <DialogTitle>{editingType ? 'Edytuj Typ Zajęć' : 'Utwórz Nowy Typ Zajęć'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              label="Nazwa Typu Zajęć"
              value={formData.friendly_name}
              onChange={(e) => {
                const friendlyName = e.target.value;
                handleInputChange('friendly_name', friendlyName);
                // Auto-generate the internal name
                handleInputChange('name', generateLessonTypeName(friendlyName));
              }}
              required
              margin="normal"
              helperText="Wprowadź opisową nazwę (np. 'Poranna Joga', 'Siła i Wytrzymałość'). Wewnętrzna nazwa zostanie wygenerowana automatycznie."
            />
            
            <TextField
              fullWidth
              label="Cena"
              type="number"
              value={formData.price}
              onChange={(e) => handleInputChange('price', e.target.value)}
              required
              margin="normal"
            />
            
            <FormControl fullWidth margin="normal" data-testid="booking-kind">
              <InputLabel id="booking-kind-label">Rodzaj zajęć</InputLabel>
              <Select
                labelId="booking-kind-label"
                value={formData.participants_per_booking ? String(formData.participants_per_booking) : 'regular'}
                label="Rodzaj zajęć"
                onChange={(e) => {
                  const kind = e.target.value;
                  handleInputChange('participants_per_booking', kind === 'regular' ? '' : kind);
                  // A duo/trio reservation takes the whole lesson.
                  if (kind !== 'regular') handleInputChange('max_people', kind);
                  // Single spots exist only on duo/trio.
                  else handleInputChange('spot_price', '');
                }}
              >
                <MenuItem value="regular">Zwykłe zajęcia — zapis na pojedyncze miejsca</MenuItem>
                <MenuItem value="2">Duo — jedna rezerwacja na całe zajęcia, 2 osoby</MenuItem>
                <MenuItem value="3">Trio — jedna rezerwacja na całe zajęcia, 3 osoby</MenuItem>
              </Select>
              <FormHelperText>
                Duo/trio: jedna osoba rezerwuje całe zajęcia (sama lub ze znajomymi) i płaci podaną cenę za całość.
                Jeśli podasz cenę za jedno miejsce, obce osoby mogą też rezerwować pojedyncze miejsca
                (wtedy całej rezerwacji już nie ma). Zwykłe karnety na nie nie działają.
              </FormHelperText>
            </FormControl>

            {formData.participants_per_booking && (
              <TextField
                fullWidth
                label="Cena za jedno miejsce (opcjonalnie)"
                type="number"
                value={formData.spot_price}
                onChange={(e) => handleInputChange('spot_price', e.target.value)}
                margin="normal"
                helperText="Puste = tylko rezerwacja całych zajęć"
                data-testid="spot-price"
              />
            )}

            <TextField
              fullWidth
              label="Maksymalna liczba osób"
              type="number"
              value={formData.max_people}
              onChange={(e) => handleInputChange('max_people', e.target.value)}
              required
              margin="normal"
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>Kolor</InputLabel>
              <Select
                value={formData.bg_color}
                onChange={(e) => {
                  if (e.target.value === 'CREATE_NEW') {
                    setShowColorCreation(true);
                  } else {
                    handleInputChange('bg_color', e.target.value);
                  }
                }}
                label="Kolor"
                required
                renderValue={(selected) => {
                  if (selected === 'CREATE_NEW') return 'Utwórz Nowy Kolor...';
                  return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <ColorPreview color={selected} />
                      {getColorLabel(selected)}
                    </div>
                  );
                }}
              >
                {availableColors.map((color) => (
                  <MenuItem key={color.id} value={color.hex_value}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <ColorPreview color={color.hex_value} />
                      {color.label}
                    </div>
                  </MenuItem>
                ))}
                <MenuItem value="CREATE_NEW" sx={{ fontStyle: 'italic', color: 'primary.main' }}>
                  + Utwórz Nowy Kolor
                </MenuItem>
              </Select>
            </FormControl>
            
            {showColorCreation && (
              <Box sx={{ mt: 2, p: 2, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Utwórz Nowy Kolor</Typography>
                
                <TextField
                  fullWidth
                  label="Nazwa Koloru"
                  value={newColorData.label}
                  onChange={(e) => {
                    const label = e.target.value;
                    setNewColorData(prev => ({ 
                      ...prev, 
                      label,
                      name: generateColorName(label)
                    }));
                  }}
                  required
                  margin="dense"
                  helperText="Opisowa nazwa dla tego koloru (np. 'Błękit Oceanu', 'Zieleń Lasu')"
                />
                
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2">Wybierz Kolor:</Typography>
                  <input
                    type="color"
                    value={newColorData.hex_value}
                    onChange={(e) => {
                      const hexValue = e.target.value;
                      setNewColorData(prev => ({ 
                        ...prev, 
                        hex_value: hexValue
                      }));
                    }}
                    style={{
                      width: '50px',
                      height: '30px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  />
                  <TextField
                    label="Wartość Hex"
                    value={newColorData.hex_value}
                    onChange={(e) => {
                      const hexValue = e.target.value;
                      setNewColorData(prev => ({ 
                        ...prev, 
                        hex_value: hexValue
                      }));
                    }}
                    variant="outlined"
                    size="small"
                    sx={{ width: '120px' }}
                  />
                  <ColorPreview color={newColorData.hex_value} />
                </Box>
                
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleCreateNewColor}
                    size="small"
                  >
                    Utwórz Kolor
                  </Button>
                  <Button 
                    variant="outlined" 
                    onClick={() => {
                      setShowColorCreation(false);
                      setNewColorData({ name: '', label: '', hex_value: '#02c2c7', is_active: true });
                    }}
                    size="small"
                  >
                    Anuluj
                  </Button>
                </Box>
              </Box>
            )}
            
            <FormControl fullWidth margin="normal">
              <InputLabel>Lokalizacja (Opcjonalne)</InputLabel>
              <Select
                value={formData.location_id}
                onChange={(e) => handleInputChange('location_id', e.target.value)}
                label="Lokalizacja (Opcjonalne)"
              >
                <MenuItem value="">Brak</MenuItem>
                {locations.map((location) => (
                  <MenuItem key={location.id} value={location.id}>
                    {location.name} - {location.address}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Autocomplete
              multiple
              options={categories}
              getOptionLabel={(option) => `${option.name} (${option.price} PLN)`}
              value={categories.filter(cat => formData.lesson_categories_id.includes(cat.id))}
              onChange={(event, newValue) => {
                handleInputChange('lesson_categories_id', newValue.map(cat => cat.id));
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={`${option.name} (${option.price} PLN)`}
                    {...getTagProps({ index })}
                    key={option.id}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Kategorie (Opcjonalne)"
                  placeholder="Wybierz kategorie..."
                  margin="normal"
                  helperText="Wybierz wiele kategorii klikając na nie. Możesz usunąć wybory klikając X na każdym chipie."
                />
              )}
              sx={{ mt: 2 }}
            />
            
            {!editingType && (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.create_price_list_entry}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        handleInputChange('create_price_list_entry', checked);
                        // Auto-fill price list fields when checkbox is checked
                        if (checked) {
                          handleInputChange('price_list_name', formData.friendly_name || formData.name);
                          handleInputChange('price_list_price', formData.price);
                        }
                      }}
                      color="primary"
                    />
                  }
                  label="Utwórz pozycję w cenniku dla tego typu zajęć"
                  sx={{ mt: 2, display: 'block' }}
                />
                
                {formData.create_price_list_entry && (
                  <Box sx={{ mt: 2, p: 2, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Ustawienia pozycji cennika</Typography>
                    
                    <TextField
                      fullWidth
                      label="Nazwa w cenniku"
                      value={formData.price_list_name}
                      onChange={(e) => handleInputChange('price_list_name', e.target.value)}
                      margin="normal"
                      helperText="Nazwa, która będzie wyświetlana w cenniku. Domyślnie taka sama jak nazwa typu zajęć."
                    />
                    
                    <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f8f9fa' }}>
                      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>
                        💰 Cena za wejście jednorazowe
                      </Typography>
                      <TextField
                        fullWidth
                        label="Cena za wejście jednorazowe"
                        type="number"
                        value={formData.price_list_price}
                        onChange={(e) => handleInputChange('price_list_price', e.target.value)}
                        margin="dense"
                        helperText="To jest cena, którą klient zapłaci za jednorazowe uczestnictwo w zajęciach tego typu."
                        sx={{ 
                          '& .MuiInputLabel-root': { 
                            fontWeight: 500 
                          }
                        }}
                      />
                    </Box>

                    {/* Pass Types Selection */}
                    {formData.lesson_categories_id.length > 0 && (
                      <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f0f8ff' }}>
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: 'info.main' }}>
                          🎟️ Dostępne karnety
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Wybierz karnety, które będą dostępne dla tego typu zajęć:
                        </Typography>
                        
                        {loadingPassTypes ? (
                          <Typography variant="body2" color="text.secondary">
                            Ładowanie dostępnych karnetów...
                          </Typography>
                        ) : availablePassTypes.length > 0 ? (
                          <Autocomplete
                            multiple
                            options={availablePassTypes}
                            getOptionLabel={(option) => `${option.name} (${option.price} PLN, ważny ${Math.ceil(option.valid_duration / (24 * 60 * 60))} dni)`}
                            value={availablePassTypes.filter(pass => formData.selected_pass_type_ids.includes(pass.id))}
                            onChange={(event, newValue) => {
                              handleInputChange('selected_pass_type_ids', newValue.map(pass => pass.id));
                            }}
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => (
                                <Chip
                                  variant="outlined"
                                  label={`${option.name} (${option.price} PLN)`}
                                  {...getTagProps({ index })}
                                  key={option.id}
                                  color="info"
                                />
                              ))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Wybierz karnety"
                                placeholder="Kliknij, aby wybrać karnety..."
                                margin="dense"
                                helperText="Wybrane karnety będą dostępne dla klientów chcących uczestniczyć w tym typie zajęć."
                              />
                            )}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            Brak dostępnych karnetów dla wybranych kategorii zajęć.
                          </Typography>
                        )}
                      </Box>
                    )}
                    
                    {/* Show selected lesson categories */}
                    {formData.lesson_categories_id.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Kategorie lekcji, które będą dodane do cennika:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                          {categories
                            .filter(cat => formData.lesson_categories_id.includes(cat.id))
                            .map((category) => (
                              <Chip
                                key={category.id}
                                variant="filled"
                                size="small"
                                label={`${category.name} (${category.price} PLN)`}
                                color="primary"
                                sx={{ 
                                  backgroundColor: 'primary.light',
                                  color: 'white',
                                  '& .MuiChip-label': {
                                    fontSize: '0.75rem'
                                  }
                                }}
                              />
                            ))}
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Te kategorie zostaną automatycznie przypisane do pozycji cennika.
                        </Typography>
                      </Box>
                    )}
                    
                    <TextField
                      fullWidth
                      label="Dodatkowe informacje (opcjonalne)"
                      value={formData.price_list_additional_info}
                      onChange={(e) => handleInputChange('price_list_additional_info', e.target.value)}
                      margin="normal"
                      multiline
                      rows={2}
                      helperText="Dodatkowe informacje, które będą wyświetlane w cenniku (np. 'Zapisy przez telefon', 'Tylko dla początkujących')."
                    />
                  </Box>
                )}
              </>
            )}
          </DialogContent>
          
          <DialogActions>
            <Button onClick={() => { setOpen(false); resetForm(); }}>Anuluj</Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? (editingType ? 'Aktualizowanie...' : 'Tworzenie...') : (editingType ? 'Aktualizuj' : 'Utwórz')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Szczegóły Typu Zajęć</DialogTitle>
        <DialogContent>
          {selectedType && (
            <Box>
              <Box mb={2} display="flex" alignItems="center">
                {selectedType.bg_color && <ColorPreview color={selectedType.bg_color} />}
                <Typography variant="h6" ml={1}>{selectedType.name}</Typography>
              </Box>
              
              {selectedType.friendly_name && (
                <Typography variant="body1" mb={1}>
                  <strong>Przyjazna Nazwa:</strong> {selectedType.friendly_name}
                </Typography>
              )}
              
              <Typography variant="body1" mb={1}>
                <strong>Cena:</strong> {selectedType.price} PLN
              </Typography>
              
              <Typography variant="body1" mb={1}>
                <strong>Maksymalna liczba osób:</strong> {selectedType.max_people}
              </Typography>

              <Typography variant="body1" mb={1}>
                <strong>Rodzaj zajęć:</strong>{' '}
                {selectedType.participants_per_booking === 2
                  ? 'Duo (rezerwacja całych zajęć)'
                  : selectedType.participants_per_booking === 3
                    ? 'Trio (rezerwacja całych zajęć)'
                    : 'Zwykłe zajęcia'}
              </Typography>

              {selectedType.participants_per_booking >= 2 && (
                <Typography variant="body1" mb={1}>
                  <strong>Pojedyncze miejsca:</strong>{' '}
                  {selectedType.spot_price != null
                    ? `Cena za miejsce: ${selectedType.spot_price} PLN`
                    : 'tylko całe zajęcia'}
                </Typography>
              )}
              
              <Typography variant="body1" mb={1}>
                <strong>Kolor:</strong> {getColorLabel(selectedType.bg_color)}
              </Typography>
              
              <Typography variant="body1" mb={1}>
                <strong>Lokalizacja:</strong> {selectedType.location 
                  ? `${selectedType.location.name}${selectedType.location.address ? ` - ${selectedType.location.address}` : ''}` 
                  : 'Brak'}
              </Typography>
              
              {selectedType.lesson_categories_id && selectedType.lesson_categories_id.length > 0 && (
                <Typography variant="body1" mb={1}>
                  <strong>Kategorie:</strong> {
                    selectedType.lesson_categories_id
                      .map(catId => {
                        const category = categories.find(cat => cat.id === catId);
                        return category ? `${category.name} (${category.price} PLN)` : `ID: ${catId}`;
                      })
                      .join(', ')
                  }
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Zamknij</Button>
          <Button 
            onClick={() => {
              setDetailsOpen(false);
              handleEdit(selectedType);
            }} 
            variant="contained" 
            color="primary"
          >
            Edytuj
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LessonTypeManagement;
